import { useEffect, useRef } from "react"
import {
  earliestExpiryMs,
  MIN_EXPIRY_REFETCH_INTERVAL_MS,
  nextTimerDelayMs,
  shouldInvalidateExpiry
} from "@/helpers/activeEffects"
import { serverNowMs } from "@/helpers/vitals"
import { useHasSession } from "@/hooks/useHasSession"
import { useAppDispatch, useAppSelector } from "@/store"
import { TAGS } from "@/store/api"
import { heroAPI, useGetHeroMeQuery } from "@/store/apis/heroAPI"
import { PlayerSelectors } from "@/store/slices/player"

export const useActiveEffectExpiry = (): void => {
  const dispatch = useAppDispatch()
  const hasSession = useHasSession()
  const isPlayerLoaded = useAppSelector(PlayerSelectors.selectIsPlayerLoaded())
  const lastInvalidated = useRef({ targetMs: 0, atMs: 0 })

  const { activeEffects, serverTime, receivedAtMs } = useGetHeroMeQuery(
    undefined,
    {
      skip: !hasSession || !isPlayerLoaded,
      selectFromResult: ({ data, fulfilledTimeStamp }) => ({
        activeEffects: data?.activeEffects,
        serverTime: data?.serverTime,
        receivedAtMs: fulfilledTimeStamp
      })
    }
  )

  useEffect(() => {
    if (!serverTime || receivedAtMs === undefined) {
      return
    }

    const endMs = earliestExpiryMs(activeEffects)
    if (endMs === null) {
      return
    }

    let timerId: number | undefined

    const tick = () => {
      const nowMs = serverNowMs(serverTime, receivedAtMs, Date.now())
      if (shouldInvalidateExpiry(lastInvalidated.current, endMs, nowMs)) {
        lastInvalidated.current = { targetMs: endMs, atMs: nowMs }
        dispatch(heroAPI.util.invalidateTags([TAGS.HERO]))
      }
      timerId = window.setTimeout(
        tick,
        nextTimerDelayMs(endMs, nowMs, MIN_EXPIRY_REFETCH_INTERVAL_MS)
      )
    }

    tick()

    return () => window.clearTimeout(timerId)
  }, [activeEffects, serverTime, receivedAtMs, dispatch])
}
